/**
 * Radio — 16 × 16 px circle (theme T5a §6).  Use for 2–5 mutually
 * exclusive options that should all be visible at once.
 *
 * Focus ring renders on the visible wrapper via `has-[input:focus-visible]`
 * since the inner `<input>` is opacity-0 (F6 audit F6-01).
 */
import type { InputHTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { cn } from './cn';

export type RadioProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { className, checked, disabled, ...rest },
  ref,
) {
  return (
    <span
      className={cn(
        'relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border bg-white transition-colors duration-fast ease-standard',
        'has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-brand-primary-400 has-[input:focus-visible]:ring-offset-2',
        checked ? 'border-brand-primary-600' : 'border-slate-400 hover:border-slate-600',
        disabled && 'cursor-not-allowed opacity-disabled',
        className,
      )}
    >
      <input
        ref={ref}
        checked={checked}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-full opacity-0 focus:outline-none"
        disabled={disabled}
        type="radio"
        {...rest}
      />
      {checked ? <span aria-hidden className="h-2 w-2 rounded-full bg-brand-primary-600" /> : null}
    </span>
  );
});
